import React, { useContext, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { QuoteContext } from '.';
import { PRIVATE_SALES, FEES } from '../constants';
import {
  DISCOUNTS,
  PrivateSaleExcludedDiscounts,
} from '../constants/DISCOUNTS';
import { SELECT_STATE } from '../firebase/fetchStates.ts';
import { SELECT_LENDER } from '../firebase/fetchLenders.ts';
import {
  getLendersForState,
  getStatesForLender,
} from '../utils/lenderUtils.ts';
import { isDiscountValidForBundle } from '../utils/isDiscountValidForBundle';
// import { isBYOBApplicable, getBYOBApplicableItems } from '../utils/byob';

export function SelectionsSection({
  splitFinanceFeature,
  allStates,
  allLenders,
}) {
  const { active, setActive, options, setOptions } = useContext(QuoteContext);

  // -------  Discount Dropdown Logic ------- //
  // Don't list BNDLE and CSS in the global Discount Dropdown
  let DISCOUNTS_DROPDOWN = Object.keys(DISCOUNTS).filter(
    (key) => !['BNDLE', 'CSS'].includes(key),
  );

  // Pre-Sale Logic Goes Here If Needed //

  // Exclude Prev Tx, 60%, and Founders discount when a Private Sale is selected
  if (active.privateSale !== 'None') {
    DISCOUNTS_DROPDOWN = DISCOUNTS_DROPDOWN.filter(
      (key) => !PrivateSaleExcludedDiscounts.includes(key),
    );
  }

  let stateLenders = getLendersForState(
    active.state.id,
    allLenders,
    active.financePlan,
  );

  // Filter states based on active lender
  const availableStates =
    active.lender.id === SELECT_LENDER.id
      ? allStates
      : getStatesForLender(allStates, active.lender, active.financePlan);

  // ---------- myMilan Logic ---------- //
  // Check if the SPLIT_FINANCE_FEATURE is enabled
  if (splitFinanceFeature) {
    // Check if Split Loan is enabled in any Option (if the split loan checkbox is checked in any Option)
    const isSplitLoanEnabled = Object.values(options).some(
      (option) => option.splitLoan.isEnabled,
    );
    if (isSplitLoanEnabled && active.myMilanSecondary) {
      stateLenders = stateLenders.filter((lender) => lender.canSplit);
    }
  }
  // ------------------------------------- //

  const onChangeHandler = (value, fieldName) => {
    switch (fieldName) {
      case 'discount':
        setActive({ ...active, discount: value });
        const optionsClone = { ...options }; // Shallow Clone
        let discount = value;
        // Update "discount" property of each area
        Object.values(optionsClone).forEach((option) => {
          // BYOB Discount Logic goes here if needed //

          option.items.forEach((area) => {
            area.discount =
              area.parentAreaId === 'bundles' &&
              !isDiscountValidForBundle(discount)
                ? 'BNDLE'
                : discount;
          });
        });
        setOptions(optionsClone);
        break;
      case 'fees':
        setActive({ ...active, fee: FEES.find((fee) => fee.id === value) });
        break;
      case 'private_sale':
        setActive({ ...active, privateSale: value });
        break;
      case 'lender':
        setActive({
          ...active,
          lender:
            stateLenders.find((lender) => lender.name === value) ||
            SELECT_LENDER,
        });
        break;
      // case 'clientStatus':
      //   setActive({ ...active, newClient: (value === 'false' || value === false) ? false : true })
      //   break
      case 'state':
        if (value === SELECT_STATE.name) return;
        const state = allStates.find((state) => state.name === value);
        // this is important when switching between states, as the active lender might have a different interest rate in the new state
        const newStateLenders = getLendersForState(
          state.id,
          allLenders,
          active.financePlan,
        );
        const lender =
          newStateLenders.find((lender) => lender.id === active.lender.id) ||
          SELECT_LENDER;
        localStorage.setItem('stateName', value);
        // ---------- For Split Finance - myMILAN is the secondary lender ---------- //
        let myMilanSecondary = newStateLenders.find(
          (lender) => lender.name === 'myMILAN',
        );
        if (!myMilanSecondary) {
          const stateLendersFor24 = getLendersForState(
            state.id,
            allLenders,
            24,
          );
          myMilanSecondary = stateLendersFor24.find(
            (lender) => lender.name === 'myMILAN',
          );
        }

        setActive({
          ...active,
          state: state,
          fee: FEES[0], // reset fee to 'no_fees' on state change
          lender: lender,
          myMilanSecondary: myMilanSecondary || null,
        });
        break;
      default:
        return;
    }
  };

  const initialValues = {
    discount: active.discount,
    fees: active.fee.id,
    private_sale: active.privateSale,
    lender: active.lender.name,
    clientStatus: active.newClient,
    state: active.state.name,
  };

  useEffect(() => {
    // Retrieve the value from localStorage
    const storedValue = localStorage.getItem('stateName');
    if (storedValue) onChangeHandler(storedValue, 'state');
  }, []);

  return (
    <section
      id='selections-section'
      className='container-fluid py-4 pt-sm-5 print-hide'
    >
      <Formik initialValues={initialValues} enableReinitialize>
        <Form className='row justify-content-center gy-4 padding-x'>
          <div className='col-auto'>
            <h3
              className={`h6 pb-1 ${
                active.state.id === SELECT_STATE.id
                  ? 'milan-text-red'
                  : 'milan-text-navy'
              }`}
            >
              State {active.state.id === SELECT_STATE.id && <sup>*</sup>}
            </h3>
            <div>
              <Field
                as='select'
                id='state'
                name='state'
                className={`form-select ${
                  active.state.id === SELECT_STATE.id ? 'border-danger' : ''
                }`}
                onChange={(e) => onChangeHandler(e.target.value, 'state')}
              >
                {availableStates.map((state) => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </Field>
            </div>
          </div>
          <div className='col-auto'>
            <h3
              className={`h6 pb-1 ${
                active.lender.id === SELECT_LENDER.id
                  ? 'milan-text-red'
                  : 'milan-text-navy'
              }`}
            >
              Lender {active.lender.id === SELECT_LENDER.id && <sup>*</sup>}
            </h3>
            <div>
              <Field
                as='select'
                id='lender'
                name='lender'
                className={`form-select ${
                  active.lender.id === SELECT_LENDER.id ? 'border-danger' : ''
                }`}
                onChange={(e) => onChangeHandler(e.target.value, 'lender')}
              >
                {stateLenders.map((lender) => (
                  <option key={lender.name} value={lender.name}>
                    {lender.name}
                  </option>
                ))}
              </Field>
            </div>
          </div>
          <div className='col-auto'>
            <h3 className='h6 pb-1 milan-text-navy'>Single Area Discount</h3>
            <div>
              <Field
                as='select'
                id='discount'
                name='discount'
                className='form-select'
                onChange={(e) => onChangeHandler(e.target.value, 'discount')}
              >
                {DISCOUNTS_DROPDOWN.map((discountKey, d) => (
                  <option key={d} value={discountKey}>
                    {discountKey}
                  </option>
                ))}
              </Field>
            </div>
          </div>
          <div className='col-auto'>
            <h3 className='h6 pb-1 milan-text-navy'>Code</h3>
            <div>
              <Field
                as='select'
                id='private_sale'
                name='private_sale'
                className='form-select'
                onChange={(e) =>
                  onChangeHandler(e.target.value, 'private_sale')
                }
                disabled={
                  active.discount === 'Prev Tx' ||
                  active.discount === '60%' ||
                  active.discount === '65%' ||
                  active.discount === 'Founders' ||
                  active.discount === 'CA EVENT - NEW'
                }
              >
                {Object.keys(PRIVATE_SALES).map((code, x) => (
                  <option key={x} value={code}>
                    {code}
                  </option>
                ))}
              </Field>
            </div>
          </div>
          {active.state.name === 'Colorado' && (
            <div className='col-auto'>
              <h3 className='h6 pb-1 milan-text-navy'>Fees</h3>
              <div>
                <Field
                  as='select'
                  id='fees'
                  name='fees'
                  className='form-select'
                  onChange={(e) => onChangeHandler(e.target.value, 'fees')}
                >
                  {FEES.map((fee) => (
                    <option key={fee.id} value={fee.id}>
                      {fee.name}
                    </option>
                  ))}
                </Field>
              </div>
            </div>
          )}
        </Form>
      </Formik>
    </section>
  );
}

export default SelectionsSection;

// ---- Pre-Sale Logic ---- //
// Don't list Event discount in the global Discount Dropdown if Pre-Sale is active
// if (active.preSale) {
//   DISCOUNTS_DROPDOWN = DISCOUNTS_DROPDOWN.filter(
//     (key) => !['Event'].includes(key)
//   );
// } else {
//   DISCOUNTS_DROPDOWN = DISCOUNTS_DROPDOWN.filter(
//     (key) => !['60%'].includes(key)
//   );
// }

// ------- BYOB (Build Your Own Bundle) Discount Logic ------- //
// Object.values(optionsClone).forEach((option) => {
//    // ---- BYOB Discount Logic ---- //
//    if (discount === 'BYOB') {
//    // Make sure none of the items areas is a bundle
//    const byobApplicableItems = getBYOBApplicableItems(option.items);
//    option.items.forEach((item) => {
//       if (isBYOBApplicable(item))
//          item.discount =
//          byobApplicableItems.length >= 3 ? discount : '50%';
//    });
//    } // End of BYOB Discount Logic
//    else {
//    // Any discount other than Military or Event is not valid for bundles
//    const validForBundles =
//       discount === 'Military' || discount === 'Event';

//    option.items.forEach((area) => {
//       area.discount =
//          area.parentAreaId === 'bundles' && !validForBundles
//          ? 'BNDLE'
//          : discount;
//    });
//    }
// });
